import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';

export type MetaProvider = 'instagram' | 'facebook' | 'facebook_ads';

export interface MetaBrandDaily {
  provider: MetaProvider;
  date: string;
  reach: number | null;
  impressions: number | null;
  profile_views: number | null;
  new_followers: number | null;
  spend: number | null;
  clicks: number | null;
  ctr: number | null;
  cpc: number | null;
  cpm: number | null;
  roas: number | null;
  purchase_value: number | null;
}

export interface MetaAccountSnapshot {
  provider: MetaProvider;
  snapshot_date: string;
  followers_count: number | null;
  follows_count: number | null;
  media_count: number | null;
}

export interface MetaAdCampaign {
  campaign_id: string;
  period_start: string;
  period_end: string;
  account_name: string | null;
  campaign_name: string;
  spend: number | null;
  impressions: number | null;
  reach: number | null;
  clicks: number | null;
  ctr: number | null;
  roas: number | null;
}

export interface MetaPost {
  media_id: string;
  provider: 'instagram' | 'facebook';
  published_at: string | null;
  media_type: string | null;
  caption: string | null;
  permalink: string | null;
  thumbnail_url: string | null;
  image_url: string | null;
  like_count: number | null;
  comments_count: number | null;
  views: number | null;
  reach: number | null;
  is_boosted: boolean | null;
}

export function useMetaBrandDaily(provider?: MetaProvider) {
  return useQuery({
    queryKey: ['meta-brand-daily', provider ?? 'all'],
    queryFn: async (): Promise<MetaBrandDaily[]> => {
      let q = marketing
        .from('meta_brand_daily')
        .select('*')
        .order('date', { ascending: true });
      if (provider) q = q.eq('provider', provider);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as MetaBrandDaily[];
    },
    staleTime: 5 * 60_000,
  });
}

export function useMetaAccountSnapshot() {
  return useQuery({
    queryKey: ['meta-account-snapshot'],
    queryFn: async (): Promise<MetaAccountSnapshot[]> => {
      const { data, error } = await marketing
        .from('meta_account_snapshot')
        .select('*')
        .order('snapshot_date', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as MetaAccountSnapshot[];
    },
    staleTime: 5 * 60_000,
  });
}

export function useMetaAdCampaigns() {
  return useQuery({
    queryKey: ['meta-ad-campaigns'],
    queryFn: async (): Promise<MetaAdCampaign[]> => {
      const { data, error } = await marketing
        .from('meta_ad_campaigns')
        .select('*')
        .order('spend', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as MetaAdCampaign[];
    },
    staleTime: 5 * 60_000,
  });
}

export function useMetaPosts(limit = 10) {
  return useQuery({
    queryKey: ['meta-posts', limit],
    queryFn: async (): Promise<MetaPost[]> => {
      const { data, error } = await marketing
        .from('meta_posts')
        .select('*')
        .order('published_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as unknown as MetaPost[];
    },
    staleTime: 5 * 60_000,
  });
}
